import { isEmpty } from 'lodash'
import {
  AuditResultPlace,
  Batch,
  Case,
  Container,
  Generic,
  Pallet,
  Place,
  Product,
  UnitOfMeasure,
  makeGeneric,
} from 'src/api/models'
import { skuit } from 'src/services/skuit'
import { Paginator } from 'src/components/Table'

import { Action, ApplyScan, Mode } from './interface'
import { Handler, HandlerInput } from './base'

export interface AuditScanItem {
  place: Generic
  container?: Generic
  sku?: Generic
  skus: Generic[]
  productCase?: Case
  batch?: Batch
  quantity: number
}

interface AuditHandlerInput extends HandlerInput {
  audit: Generic
}

export class AuditHandler extends Handler {
  audit: Generic
  currentPlace?: Generic
  currentPlaceStarted?: boolean
  currentPlaceStatus?: string
  currentPlaceAccuracyScore?: number | string
  currentContainer?: Generic
  mode: Mode
  lastCaseMap: { [product: string]: Case | undefined }
  lastSkuMap: { [product: string]: Generic | undefined }
  lastBatchMap: { [batch: string]: Batch | undefined }
  toMeasure?: Generic
  scans: { [key: string]: AuditScanItem }
  placePaginator?: Paginator
  unitPaginator?: Paginator
  containerPaginator?: Paginator

  constructor(props: AuditHandlerInput) {
    super(props)
    this.audit = props.audit
    this.mode = Mode.Scan
    this.lastCaseMap = {}
    this.scans = {}
    this.lastSkuMap = {}
    this.lastBatchMap = {}
  }

  key = (item: AuditScanItem) => {
    return `${item.place.object.guid}-${item.container?.object.guid}-${item.sku?.object.guid}-${item.productCase?.guid}-${item.skus?.length}-${item.batch?.guid}`
  }

  scan = (item: AuditScanItem) => {
    const key = this.key(item)
    const record = this.scans[key]
    if (this.mode !== Mode.Scan) {
      item.quantity = item.quantity * -1
    }
    if (record) {
      record.quantity += item.quantity
      if (record.quantity === 0) {
        delete this.scans[key]
      }
    } else {
      this.scans[key] = item
    }
  }

  apply = ({ scanned, context, local }: ApplyScan): [Action, any | undefined] => {
    switch (scanned.object.type) {
      case Place.type: {
        if (!this.currentPlace && context?.status) {
          this.currentPlace = scanned
          this.currentPlaceStarted = context?.started
          this.currentPlaceStatus = context?.status
          this.currentPlaceAccuracyScore = context?.accuracyScore
        }
        this.notify({})
        return [Action.Invalid, undefined]
      }
      case Pallet.type:
      case Container.type: {
        if (this.currentPlace) {
          this.currentContainer = scanned
          this.scan({
            place: this.currentPlace,
            container: this.currentContainer,
            quantity: local?.quantity !== undefined ? local.quantity : 1,
            skus: [],
          })
          this.notify({})
          return [Action.None, undefined]
        }
        return [Action.Invalid, undefined]
      }
      case Case.type:
      case Product.type: {
        if (!this.currentPlace) {
          return [Action.Invalid, undefined]
        }
        const product = context?.product || scanned
        const sku =
          this.lastSkuMap[product.object.guid] ||
          (context?.skus?.length === 1 ? context.skus[0] : context?.sku)
        const skus = context?.skus || (sku ? [sku] : [])
        const productCase =
          scanned.objectType === Case.type ? scanned.object : this.lastCaseMap[product.object.guid]
        const batch =
          this.lastBatchMap[product.object.guid] || context?.batch
        if (context?.sku?.object.unitOfMeasure === UnitOfMeasure.lbs) {
          if (local?.quantity) {
            this.scan({
              place: this.currentPlace,
              container: this.currentContainer,
              sku,
              skus,
              batch,
              productCase,
              quantity: local.quantity,
            })
          } else {
            this.toMeasure = sku
          }
        } else {
          this.scan({
            place: this.currentPlace,
            container: this.currentContainer,
            sku,
            skus,
            batch,
            productCase,
            quantity: 1,
          })
        }
        this.notify({})
        return [Action.None, undefined]
      }
    }
    return [Action.Invalid, undefined]
  }

  applyAudit = (context?: { place: Place; started: boolean; status: string }) => {
    const place = context?.place
    const started = context?.started
    if (place) {
      this.currentPlace = makeGeneric(place)
      this.currentPlaceStarted = started
      this.currentPlaceStatus = context?.status
      this.currentContainer = undefined
      this.notify({})
    }
    return this
  }

  startPlace = async () => {
    const place = this.currentPlace
    const response = await skuit.sc.audits.places.start(this.audit.object.guid, place?.object.guid)
    this.cache.clear(this.currentPlace?.object.guid)
    this.currentPlaceStarted = true
    this.currentPlaceStatus = 'started'
    this.currentPlaceAccuracyScore = undefined
    this.notify({})
    return response
  }

  restartPlace = async () => {
    const place = this.currentPlace
    const response = await skuit.sc.audits.places.restart(
      this.audit.object.guid,
      place?.object.guid
    )
    this.cache.clear(this.currentPlace?.object.guid)
    this.cache.clear(this.currentContainer?.object.guid)
    this.currentPlaceStarted = true
    this.currentPlaceStatus = 'started'
    this.currentPlaceAccuracyScore = undefined
    this.currentContainer = undefined
    this.scans = {}
    this.unitPaginator?.refresh()
    this.containerPaginator?.refresh()
    this.notify({})
    return response
  }

  clearPlace = () => {
    if (!isEmpty(this.scans)) {
      throw new Error('You have unsaved changes.')
    }
    this.currentPlace = undefined
    this.currentContainer = undefined
    this.currentPlaceStarted = undefined
    this.currentPlaceStatus = undefined
    this.currentPlaceAccuracyScore = undefined
    this.scans = {}
    this.notify({})
  }

  resetPlace = () => {
    this.cache.clear(this.currentContainer?.object.guid)
    this.currentContainer = undefined
    this.scans = {}
    this.notify({})
  }

  clearContainer = () => {
    this.cache.clear(this.currentContainer?.object.guid)
    this.currentContainer = undefined
    this.notify({})
  }

  setMode = (mode: Mode) => {
    this.mode = mode
    this.notify({})
  }

  swapMode = () => {
    this.mode = this.mode === Mode.Scan ? Mode.Unscan : Mode.Scan
    this.notify({})
  }

  setSku = (scan: AuditScanItem, sku: Generic) => {
    scan.sku = sku
    this.lastSkuMap[sku.object.product] = sku
    this.notify({})
  }

  updateScan = (scan: AuditScanItem, count: number) => {
    scan.quantity = count
    this.notify({})
  }

  caseSelector = (item: AuditScanItem) => {
    return (from?: Case, to?: Case) => {
      const fromItem = { ...item, productCase: from, quantity: 1 }
      this.swapMode()
      this.scan(fromItem)

      const productCase = to?.type === Case.type ? to : undefined
      const toItem = { ...item, productCase, quantity: 1 }
      this.swapMode()
      this.scan(toItem)
      this.lastCaseMap[item.sku?.object.product.guid] = productCase
      this.notify({})
    }
  }

  batchSelector = (item: AuditScanItem) => {
    return (from?: Batch, to?: Batch) => {
      const fromItem = { ...item, batch: from, quantity: 1 }
      this.swapMode()
      this.scan(fromItem)

      const batch = to?.type === Batch.type ? to : undefined
      const toItem = { ...item, batch, quantity: 1 }
      this.swapMode()
      this.scan(toItem)
      this.lastBatchMap[item.sku?.object.product.guid] = batch
      this.notify({})
    }
  }

  save = async (clearPlace = true) => {
    const response = await skuit.sc.audits.scan(
      this.audit.object.guid,
      Object.values(this.scans).map((item: AuditScanItem) => {
        console.log(item)
        const caseQuantity = item.productCase ? item.productCase.numUnits : 1
        if (isNaN(item.quantity)) {
          throw new Error('Invalid quantity!')
        }
        return {
          place: item.place.object.guid,
          container: item.container?.object.guid,
          sku: item.sku?.object.guid,
          num_units: item.quantity * caseQuantity,
          batch: item.batch?.guid
        }
      })
    )
    if (clearPlace) {
      this.clearPlace()
    }
    this.scans = {}
    this.unitPaginator?.refresh()
    this.containerPaginator?.refresh()
    this.clearContainer()
    return response
  }

  complete = async () => {
    const place = this.currentPlace
    if (!isEmpty(this.scans)) {
      throw new Error('You have unsaved changes.')
    }
    const response = await skuit.sc.audits.places.complete(
      this.audit.object.guid,
      place?.object.guid
    )
    this.unitPaginator?.refresh()
    this.containerPaginator?.refresh()
    this.currentPlaceStatus = 'completed'
    this.currentPlaceAccuracyScore = response.accuracyScore
    this.notify({})
    return response
  }

  uncomplete = async (place: Place) => {
    const response = await skuit.sc.audits.places.uncomplete(this.audit.object.guid, place.guid)
    this.cache.clear(this.currentPlace?.object.guid)
    this.currentPlaceStatus = 'started'
    this.currentPlaceAccuracyScore = undefined
    this.notify({})
    return response
  }

  reconcile = async (arPlace: AuditResultPlace) => {
    const response = await skuit.sc.audits.places.reconcile(
      this.audit.object.guid,
      arPlace.place.guid
    )
    this.cache.clear(this.currentPlace?.object.guid)
    this.clearPlace()
    return response
  }

  reconcileCurrent = async () => {
    const response = await skuit.sc.audits.places.reconcile(
      this.audit.object.guid,
      this.currentPlace?.object.guid
    )
    this.cache.clear(this.currentPlace?.object.guid)
    this.clearPlace()
    return response
  }

  getContext = () => {
    return {
      activity: 'auditing',
      audit: this.audit?.object?.guid,
      place: this.currentPlace?.object.guid,
      container: this.currentContainer?.object.guid,
    }
  }
}
